import { compactGeometry } from "@project/emerge/src/util/compactGeometry";
import * as THREE from "three";
import {
  createFoamBlockGeometry,
  createFoamBlockOutline,
  FOAM_OCCLUSION_ATTRIBUTE,
  solidProjectionUv,
} from "./foamBlockGeometry";
import {
  SLOT_DEPTH_MM,
  SLOT_MIN_FLOOR_MM,
  SLOT_PITCH_MM,
  SLOT_WIDTH_MM,
  slotFloorVisibility,
  slotShade,
  slotWallVisibility,
} from "./slotSurface";

/**
 * Rows a groove wall is split into, top to bottom.
 *
 * The occlusion down a wall is not linear: it drops from half the sky at the
 * lip to almost none within the first few millimetres and stays there. Two
 * vertices with the shading interpolated between them would light the upper
 * wall far too much; six rows follow the curve closely enough at 10 mm deep.
 */
const WALL_ROWS = 6;

function slotCells(span: number): number {
  return Math.max(1, Math.round(span / SLOT_PITCH_MM));
}

/**
 * Accumulates flat-shaded quads with the foam attributes attached.
 *
 * Every face gets its own four vertices, so every edge of the pad is hard —
 * which a saw cut is. The winding is fixed from the normal handed in, so the
 * caller can list the corners in any order it likes.
 */
class QuadBuilder {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly uvs: number[] = [];
  private readonly occlusions: number[] = [];
  private readonly indices: number[] = [];
  private readonly scratch = new THREE.Vector3();
  private readonly edgeA = new THREE.Vector3();
  private readonly edgeB = new THREE.Vector3();

  /**
   * @param corners Four corners around the quad, in either winding.
   * @param normal Outward normal, shared by all four.
   * @param occlusion Per corner, in the order the corners were given.
   */
  quad(
    corners: readonly [
      THREE.Vector3,
      THREE.Vector3,
      THREE.Vector3,
      THREE.Vector3,
    ],
    normal: THREE.Vector3,
    occlusion: readonly [number, number, number, number],
  ): void {
    const [cornerA, cornerB, cornerC, cornerD] = corners;
    this.edgeA.subVectors(cornerB, cornerA);
    this.edgeB.subVectors(cornerD, cornerA);
    const flip =
      this.scratch.crossVectors(this.edgeA, this.edgeB).dot(normal) < 0;
    const order = flip
      ? [cornerA, cornerD, cornerC, cornerB]
      : [cornerA, cornerB, cornerC, cornerD];
    const shade = flip
      ? [occlusion[0], occlusion[3], occlusion[2], occlusion[1]]
      : occlusion;

    const base = this.positions.length / 3;
    order.forEach((p, i) => {
      this.positions.push(p.x, p.y, p.z);
      this.normals.push(normal.x, normal.y, normal.z);
      const [u, v] = solidProjectionUv(normal, p);
      this.uvs.push(u, v);
      this.occlusions.push(shade[i] ?? 1);
    });
    this.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  build(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(this.positions, 3),
    );
    geometry.setAttribute(
      "normal",
      new THREE.Float32BufferAttribute(this.normals, 3),
    );
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(this.uvs, 2));
    geometry.setAttribute(
      FOAM_OCCLUSION_ATTRIBUTE,
      new THREE.Float32BufferAttribute(this.occlusions, 1),
    );
    geometry.setIndex(this.indices);
    return geometry;
  }
}

const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
const PLUS_X = v(1, 0, 0);
const MINUS_X = v(-1, 0, 0);
const PLUS_Y = v(0, 1, 0);
const MINUS_Y = v(0, -1, 0);
const PLUS_Z = v(0, 0, 1);
const MINUS_Z = v(0, 0, -1);
const OPEN: readonly [number, number, number, number] = [1, 1, 1, 1];

/**
 * Geschlitzter Schaumstoff als echte Geometrie: ein Block mit eingesägten Nuten.
 *
 * The grooves run along local x and are spaced along z, one to each
 * {@link SLOT_PITCH_MM} cell and centred in it, so the outermost ribs are
 * half a cell wide. They are cut into local -y, the face that shows (see
 * `createSpikedPadGeometry` for why), {@link SLOT_DEPTH_MM} deep.
 *
 * As with the nubs, three things are baked per vertex: the normal (flat per
 * face — a saw cut is a plane), the pore coordinate projected in millimetres,
 * and the occlusion inside the grooves, which at 7 mm wide and 10 mm deep is
 * heavy and beyond what the screen-space pass can resolve. The material variant
 * that reads them is `getGroovedFoamMaterial`.
 *
 * @param width Pad width in mm, along x — the grooves' length.
 * @param thickness Pad thickness in mm, along y.
 * @param depth Pad depth in mm, along z — the axis the grooves repeat on.
 */
/**
 * Whether a pad this thick is modelled with real grooves.
 *
 * Exported because the renderer has to pick a material that agrees with the
 * geometry: below this thickness the pad falls back to a plain block, and the
 * material that expects the grooves to be in the mesh would paint nothing at
 * all onto it.
 */
export function slottedPadHasGrooves(thickness: number): boolean {
  return thickness - SLOT_DEPTH_MM >= SLOT_MIN_FLOOR_MM;
}

export function createSlottedPadGeometry(
  width: number,
  thickness: number,
  depth: number,
): THREE.BufferGeometry {
  const cut = SLOT_DEPTH_MM;
  if (!slottedPadHasGrooves(thickness)) {
    // Too thin to be this sheet with its grooves in it.
    return createFoamBlockGeometry(width, thickness, depth);
  }

  const cells = slotCells(depth);
  const pitch = depth / cells;
  const half = Math.min(SLOT_WIDTH_MM, pitch / 2) / 2;
  const slotWidth = half * 2;

  const x0 = -width / 2;
  const x1 = width / 2;
  const z0 = -depth / 2;
  const z1 = depth / 2;
  const face = -thickness / 2;
  const floor = face + cut;
  const back = thickness / 2;

  const wallShades = Array.from({ length: WALL_ROWS + 1 }, (_, row) =>
    slotShade(slotWallVisibility((cut * row) / WALL_ROWS, slotWidth)),
  );
  const floorShade = slotShade(slotFloorVisibility(cut, slotWidth));

  const quads = new QuadBuilder();

  // The glued face and the two ends the grooves run out of.
  quads.quad(
    [v(x0, back, z0), v(x1, back, z0), v(x1, back, z1), v(x0, back, z1)],
    PLUS_Y,
    OPEN,
  );
  quads.quad(
    [v(x0, face, z0), v(x1, face, z0), v(x1, back, z0), v(x0, back, z0)],
    MINUS_Z,
    OPEN,
  );
  quads.quad(
    [v(x0, face, z1), v(x1, face, z1), v(x1, back, z1), v(x0, back, z1)],
    PLUS_Z,
    OPEN,
  );

  // The two sides that show the profile: the floor band, then a tab per strip.
  quads.quad(
    [v(x0, floor, z0), v(x0, floor, z1), v(x0, back, z1), v(x0, back, z0)],
    MINUS_X,
    OPEN,
  );
  quads.quad(
    [v(x1, floor, z0), v(x1, floor, z1), v(x1, back, z1), v(x1, back, z0)],
    PLUS_X,
    OPEN,
  );

  // Strips of face between the grooves, and the grooves themselves.
  let stripStart = z0;
  for (let i = 0; i <= cells; i++) {
    const centre = z0 + (i + 0.5) * pitch;
    const stripEnd = i < cells ? centre - half : z1;

    quads.quad(
      [
        v(x0, face, stripStart),
        v(x1, face, stripStart),
        v(x1, face, stripEnd),
        v(x0, face, stripEnd),
      ],
      MINUS_Y,
      OPEN,
    );
    quads.quad(
      [
        v(x0, face, stripStart),
        v(x0, face, stripEnd),
        v(x0, floor, stripEnd),
        v(x0, floor, stripStart),
      ],
      MINUS_X,
      OPEN,
    );
    quads.quad(
      [
        v(x1, face, stripStart),
        v(x1, face, stripEnd),
        v(x1, floor, stripEnd),
        v(x1, floor, stripStart),
      ],
      PLUS_X,
      OPEN,
    );

    if (i === cells) break;

    const near = centre - half;
    const far = centre + half;

    quads.quad(
      [
        v(x0, floor, near),
        v(x1, floor, near),
        v(x1, floor, far),
        v(x0, floor, far),
      ],
      MINUS_Y,
      [floorShade, floorShade, floorShade, floorShade],
    );

    for (let row = 0; row < WALL_ROWS; row++) {
      const tTop = (cut * row) / WALL_ROWS;
      const tBottom = (cut * (row + 1)) / WALL_ROWS;
      const yTop = face + tTop;
      const yBottom = face + tBottom;
      const shadeTop = wallShades[row]!;
      const shadeBottom = wallShades[row + 1]!;
      const shade: readonly [number, number, number, number] = [
        shadeTop,
        shadeTop,
        shadeBottom,
        shadeBottom,
      ];
      // The near wall belongs to the strip before the groove and faces into it.
      quads.quad(
        [
          v(x0, yTop, near),
          v(x1, yTop, near),
          v(x1, yBottom, near),
          v(x0, yBottom, near),
        ],
        PLUS_Z,
        shade,
      );
      quads.quad(
        [
          v(x0, yTop, far),
          v(x1, yTop, far),
          v(x1, yBottom, far),
          v(x0, yBottom, far),
        ],
        MINUS_Z,
        shade,
      );
    }

    stripStart = far;
  }

  return compactGeometry(quads.build());
}

/**
 * Kantenlinien für ein genutetes Polster: der Block, wie er bestellt wurde.
 *
 * Every groove edge is a right angle, so outlining the mesh itself would draw
 * four lines per groove across the pad. The grooves are plain to see without
 * them; the block's own outline is what the drawing wants.
 */
export function createSlottedPadOutline(
  width: number,
  thickness: number,
  depth: number,
): THREE.BufferGeometry {
  return createFoamBlockOutline(width, thickness, depth);
}
